import { useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ProjectForm from "../ProjectForm/ProjectForm";
import useSaveProject from "../ProjectForm/api/useSaveProject";
import type { NewProjectValues } from "../ProjectForm/types";

const FORM_ID = "create-project-form";
// Long enough to read the sales executive warning before the page changes.
const WARNING_DELAY_MS = 2500;

const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;

const CreateProjectV2 = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useSaveProject();
  const [submitting, setSubmitting] = useState(false);
  // Guards a second submit in the same tick, before `submitting` re-renders.
  const inFlight = useRef(false);
  // "Start Scoping from Blueprint" submits the same form; this says where to go.
  const destination = useRef<"edit" | "blueprint">("edit");
  const [toast, setToast] = useState<{
    variant: "warning" | "danger";
    text: string;
  } | null>(null);

  const handleSubmit = async (values: NewProjectValues) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setToast(null);
    const path =
      destination.current === "blueprint" ? "task_blueprints/new" : "edit";
    destination.current = "edit";
    try {
      const { projectId, warning } = await create(values);
      const redirect = () =>
        window.location.replace(`${appHost}/projects/${projectId}/${path}`);
      if (warning) {
        setToast({ variant: "warning", text: warning });
        setTimeout(redirect, WARNING_DELAY_MS);
      } else {
        redirect();
      }
    } catch (err) {
      setToast({
        variant: "danger",
        text:
          err instanceof Error
            ? err.message
            : "The project could not be created.",
      });
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const startScoping = () => {
    destination.current = "blueprint";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
    // requestSubmit runs handleSubmit synchronously, so it has already read
    // this; reset it in case validation stopped the submit.
    destination.current = "edit";
  };

  return (
    // The main-nav page treatment (grey background, padding) the project list uses.
    <div className="admin-list-card--page">
      <AdminFormPage
        title="Create Project"
        formId={FORM_ID}
        onCancel={() => navigate("/projects")}
        saveDisabled={submitting}
        extraActions={
          <Button
            className="btnSeawhite"
            type="button"
            onClick={startScoping}
            disabled={submitting}
          >
            Start Scoping from Blueprint
          </Button>
        }
      >
        <ProjectForm
          id={FORM_ID}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CreateProjectV2;
